import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet-imageoverlay-rotated';
import { X, Map as MapIcon, Upload, Trash2, Crosshair, Eye, EyeOff } from 'lucide-react';
import { compressImage, uid } from '../lib/model';
import { mPerLat, mPerLon } from '../lib/geo';
import { pdfToImage } from './FloorPlans';
import { portalRoot } from './TourGuide';
import MapDocsExtra from './MapDocsExtra';
import { modeId } from '../lib/profiles';

export const openSitePlans = () => window.dispatchEvent(new Event('giss-site-plans'));
const TILE = 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}';
const dims = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res([i.naturalWidth, i.naturalHeight]); i.onerror = rej; i.src = src; });

// two plan pixels + the same two points on the map -> similarity transform (scale, rotation, shift) -> image corners
export function siteCorners(w, h, px, ll) {
  const o = ll[0], ky = mPerLat(o[0]), kx = mPerLon(o[0]);
  const M = ll.map(([la, lo]) => [(lo - o[1]) * kx, (la - o[0]) * ky]), P = px.map(([x, y]) => [x, -y]);
  const dp = [P[1][0] - P[0][0], P[1][1] - P[0][1]], dm = [M[1][0] - M[0][0], M[1][1] - M[0][1]], n = dp[0] ** 2 + dp[1] ** 2;
  if (!n) return null;
  const a = [(dm[0] * dp[0] + dm[1] * dp[1]) / n, (dm[1] * dp[0] - dm[0] * dp[1]) / n];
  const f = ([x, y]) => { const q = [x - P[0][0], -y - P[0][1]], e = M[0][0] + a[0] * q[0] - a[1] * q[1], nn = M[0][1] + a[1] * q[0] + a[0] * q[1]; return [o[0] + nn / ky, o[1] + e / kx]; };
  return { tl: f([0, 0]), tr: f([w, 0]), bl: f([0, h]), mpp: Math.hypot(...a) };
}

// map: aligned site plans over the aerial image (only when no storey plan is shown)
export function useSiteLayer(mapRef, sites) {
  const key = (sites || []).filter((s) => s.c && s.on !== false).map((s) => `${s.id}|${s.op}|${s.c.tl}|${s.c.tr}|${s.c.bl}`).join(';');
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !key) return undefined;
    const ls = sites.filter((s) => s.c && s.on !== false).map((s) => L.imageOverlay.rotated(s.src, L.latLng(s.c.tl), L.latLng(s.c.tr), L.latLng(s.c.bl), { opacity: s.op ?? 0.7, interactive: false }).addTo(map));
    return () => ls.forEach((l) => l.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, key]);
}

function MiniMap({ center, pts, onTap }) {
  const el = useRef(null), map = useRef(null), mk = useRef([]);
  useEffect(() => {
    map.current = L.map(el.current, { zoomControl: true, attributionControl: false }).setView(center, 19);
    L.tileLayer(TILE, { maxZoom: 22, maxNativeZoom: 20 }).addTo(map.current);
    map.current.on('click', (e) => onTap([e.latlng.lat, e.latlng.lng]));
    return () => map.current.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { mk.current.forEach((m) => m.remove()); mk.current = pts.map((p, i) => L.marker(p, { icon: L.divIcon({ className: 'site-pin', html: `<b>${'AB'[i]}</b>`, iconSize: [26, 26] }) }).addTo(map.current)); }, [pts]);
  return <div ref={el} className="site-mini" data-testid="site-mini-map" />;
}

function Align({ t, s, center, onDone }) {
  const [px, setPx] = useState([]), [ll, setLl] = useState([]);
  const tap = (e) => { const r = e.currentTarget.getBoundingClientRect(), p = [((e.clientX - r.left) / r.width) * s.w, ((e.clientY - r.top) / r.height) * s.h]; setPx((a) => (a.length >= 2 ? [p] : [...a, p])); };
  const ok = px.length === 2 && ll.length === 2;
  return (
    <div className="site-align" data-testid={`site-align-${s.id}`}>
      <p className="muted small-hint">{t.spAlignHint}</p>
      <div className="site-two">
        <div className="fp-img-wrap" onClick={tap} data-testid="site-align-img">
          <img src={s.src} alt="" draggable={false} />
          <svg viewBox={`0 0 ${s.w} ${s.h}`} preserveAspectRatio="none">{px.map((p, i) => <g key={i}><circle cx={p[0]} cy={p[1]} r={s.w / 70} fill="#dc2626" stroke="#fff" strokeWidth={s.w / 400} /><text x={p[0] + s.w / 50} y={p[1]} fontSize={s.w / 30} fill="#dc2626" fontWeight="bold">{'AB'[i]}</text></g>)}</svg>
        </div>
        <MiniMap center={center} pts={ll} onTap={(p) => setLl((a) => (a.length >= 2 ? [p] : [...a, p]))} />
      </div>
      <button className="btn btn-green btn-sm" data-testid="site-align-apply" disabled={!ok} onClick={() => { const c = siteCorners(s.w, s.h, px, ll); if (!c) return toast.error(t.skBad); onDone(c); }}><Crosshair size={14} /> {t.spApply}</button>
    </div>
  );
}

// site plan (PDF / image) placed true to position over the map: service line and garden pipe are drawn on it
export default function SitePlans({ t, project, updateProject, mapRef }) {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [al, setAl] = useState(null);
  const file = useRef(null);
  useEffect(() => { const h = () => setOn(true); window.addEventListener('giss-site-plans', h); return () => window.removeEventListener('giss-site-plans', h); }, []);
  if (!on) return null;
  const list = project.sites || [];
  const set = (id, p) => updateProject((pr) => ({ ...pr, sites: (pr.sites || []).map((x) => (x.id === id ? { ...x, ...p } : x)) }));
  const c0 = mapRef.current?.getCenter?.(), center = c0 ? [c0.lat, c0.lng] : [project.lat ?? 48.137, project.lon ?? 11.575];
  const add = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const src = /pdf$/i.test(f.type) || /\.pdf$/i.test(f.name) ? await pdfToImage(f) : (await compressImage(f, 2400, 0.85, '#ffffff')).data, [w, h] = await dims(src);
      const s = { id: uid('site'), name: f.name.replace(/\.[^.]+$/, ''), src, w, h, op: 0.7, c: null };
      updateProject((pr) => ({ ...pr, sites: [...(pr.sites || []), s] })); setAl(s.id);
    } catch { toast.error(t.fpFail); }
    setBusy(false);
  };
  return createPortal(
    <div className="editor-backdrop" data-testid="site-plans-dialog">
      <div className="editor irr-bom site-dlg">
        <div className="editor-head"><h4><MapIcon size={18} /> {modeId() === 'photo' ? t.mdTitle : t.spTitle}</h4><button className="icon-btn" data-testid="site-plans-close-btn" onClick={() => setOn(false)}><X size={16} /></button></div>
        <div className="row">
          <button className="btn btn-green btn-sm" data-testid="site-upload-btn" disabled={busy} onClick={() => file.current.click()}><Upload size={14} /> {busy ? '…' : t.spUpload}</button>
          <input ref={file} type="file" hidden accept="image/*,application/pdf" data-testid="site-file-input" onChange={(e) => { add(e.target.files[0]); e.target.value = ''; }} />
        </div>
        {!list.length && <p className="empty" data-testid="site-empty">{t.spEmpty}</p>}
        {list.map((s) => (
          <div key={s.id} className="irr-zone" data-testid={`site-item-${s.id}`}>
            <div className="row">
              <input value={s.name} data-testid={`site-rename-${s.id}`} onChange={(e) => set(s.id, { name: e.target.value })} />
              <button className="btn btn-outline btn-sm" data-testid={`site-align-btn-${s.id}`} onClick={() => setAl(al === s.id ? null : s.id)}><Crosshair size={14} /> {t.spAlign}</button>
              <button className="icon-btn" data-testid={`site-toggle-${s.id}`} onClick={() => set(s.id, { on: s.on === false })}>{s.on === false ? <EyeOff size={15} /> : <Eye size={15} />}</button>
              <button className="icon-btn danger" data-testid={`site-del-${s.id}`} onClick={() => updateProject((pr) => ({ ...pr, sites: (pr.sites || []).filter((x) => x.id !== s.id) }))}><Trash2 size={14} /></button>
            </div>
            <p className="sw-res" data-testid={`site-state-${s.id}`}>{s.c ? `${t.spAligned} · ${String(Math.round(s.w * s.c.mpp)).replace('.', ',')} × ${String(Math.round(s.h * s.c.mpp)).replace('.', ',')} m` : t.spNotAligned}</p>
            {s.c && <label className="mo-op">{t.moOpacity}<input type="range" min="0.2" max="1" step="0.05" data-testid={`site-opacity-${s.id}`} value={s.op ?? 0.7} onChange={(e) => set(s.id, { op: +e.target.value })} /></label>}
            {al === s.id && <Align t={t} s={s} center={center} onDone={(c) => { set(s.id, { c, on: true }); setAl(null); toast.success(t.spAlignedOk); }} />}
          </div>
        ))}
        <p className="muted small-hint">{t.spHint}</p>
        {modeId() === 'photo' && <MapDocsExtra t={t} project={project} updateProject={updateProject} getCenter={() => mapRef.current?.getCenter?.()} />}
      </div>
    </div>,
    portalRoot(),
  );
}
